import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { ArrowLeft, Users, Heart, Shield, Eye, Search, MapPin, Smartphone } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_locate-loved-ones-3/artifacts/5wufybp4_LOGO.jpeg";

export default function AboutPage() {
  const [stats, setStats] = useState({ total_cases: 0, found_cases: 0, active_cases: 0, total_sightings: 0 });

  useEffect(() => {
    axios.get(`${API}/stats`).then(r => setStats(r.data)).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-slate-100">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <img src={LOGO_URL} alt="MISYOU" className="w-8 h-8 rounded-lg object-cover" />
              <span className="font-bold text-slate-900">MISYOU</span>
            </Link>
            <Link to="/">
              <Button variant="ghost" size="sm">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Home
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 pb-24">
        <div className="text-center mb-12">
          <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Heart className="w-8 h-8 text-primary" />
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-3">About MISYOU</h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            A community-driven platform helping families reunite with their missing loved ones.
          </p>
        </div>

        <Card className="shadow-lg border-slate-100 mb-8">
          <CardContent className="p-8 sm:p-12">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">Our Mission</h2>
            <p className="text-slate-700 leading-relaxed mb-4">
              Every year, thousands of people go missing in India alone. Behind every statistic is a family
              waiting by the phone, a community holding hope, and a person who deserves to be found.
              MISYOU exists to make that search faster, easier, and more effective.
            </p>
            <p className="text-slate-700 leading-relaxed">
              We connect families, neighbours, volunteers, and authorities through a single, easy-to-use
              platform — so that no missing-person case has to rely on flyers, word-of-mouth, or chance alone.
            </p>
          </CardContent>
        </Card>

        {/* Public safety statement against scams */}
        <Card className="shadow-lg border-2 border-red-200 bg-red-50/40 mb-8" data-testid="about-safety-statement">
          <CardContent className="p-8 sm:p-12">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-red-100 text-red-700 flex items-center justify-center shrink-0">
                <Shield className="w-6 h-6" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-red-900 mb-2">Your safety is our priority</h2>
                <p className="text-slate-800 leading-relaxed mb-3">
                  <strong>We never share your phone number publicly.</strong> Anyone who contacts you claiming to
                  have information about your case and demands money <strong>is a scammer</strong> — please
                  report them immediately using the "Report Abuse" button on your case page.
                </p>
                <p className="text-base sm:text-lg font-bold text-red-900 leading-snug bg-white/70 border-l-4 border-red-600 px-3 py-2 rounded">
                  MISYOU advises you — Don&apos;t give any money to anyone without involving your local police station, always keep your local police informed.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
          {[
            { label: 'Cases reported', value: stats.total_cases },
            { label: 'Active searches', value: stats.active_cases },
            { label: 'Found safe', value: stats.found_cases, accent: 'text-emerald-600' },
            { label: 'Sightings logged', value: stats.total_sightings },
          ].map((s) => (
            <Card key={s.label} className="border-slate-100">
              <CardContent className="p-5 text-center">
                <p className={`text-3xl font-bold ${s.accent || 'text-slate-900'}`}>{s.value}</p>
                <p className="text-xs text-slate-500 mt-1">{s.label}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        <Card className="shadow-lg border-slate-100 mb-8">
          <CardContent className="p-8 sm:p-12">
            <h2 className="text-2xl font-bold text-slate-900 mb-6">How MISYOU works</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {[
                { icon: Users, title: 'Report a case', body: 'Families create a detailed report with photos, last-seen location, and identifying information. We verify every case to protect against misuse.' },
                { icon: Search, title: 'Community search', body: 'Verified cases are visible to a growing network of users who can search by name, location, or filter by age, gender, and urgency.' },
                { icon: Eye, title: 'Submit a sighting', body: 'Anyone who recognises a missing person can submit a sighting, anonymously if needed. The reporting family is notified instantly.' },
                { icon: Shield, title: 'Privacy & moderation', body: 'Photos can be blurred for cases past their visibility window. Moderators review flagged content. Your data is protected at every step.' },
              ].map((step) => {
                const Icon = step.icon;
                return (
                  <div key={step.title} className="flex gap-4">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
                      <Icon className="w-5 h-5" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900 mb-1">{step.title}</h3>
                      <p className="text-sm text-slate-600 leading-relaxed">{step.body}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-lg border-slate-100 mb-8">
          <CardContent className="p-8 sm:p-12">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">Built for India, available everywhere</h2>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                <p className="text-sm text-slate-700">Headquartered in Punjab, India. Cases supported nationwide and accessible from anywhere in the world.</p>
              </div>
              <div className="flex items-start gap-3">
                <Smartphone className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                <p className="text-sm text-slate-700">Available on Google Play and in your browser — use MISYOU on any phone, tablet, or desktop, wherever you are.</p>
              </div>
              <div className="flex items-start gap-3">
                <Shield className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                <p className="text-sm text-slate-700">JWT authentication, role-based moderation, photo masking, and detailed legal protections by design.</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-lg border-slate-100 bg-primary/5">
          <CardContent className="p-8 sm:p-12 text-center">
            <h2 className="text-2xl font-bold text-slate-900 mb-3">Help us help more families</h2>
            <p className="text-slate-700 mb-6 max-w-xl mx-auto">
              Whether you're searching for someone, recognise a face, or want to spread the word — every contribution moves a family closer to a reunion.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/register"><Button size="lg" data-testid="about-register-btn">Get started — it's free</Button></Link>
              <Link to="/qr"><Button size="lg" variant="outline" data-testid="about-qr-btn">Share MISYOU (QR)</Button></Link>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
